import { useState } from "react";

// HOOKS
import useScreenSize from "../../../../hooks/useScreenSize";

// REDUX
import { useDispatch } from "react-redux";
import {
  updateCryptoStatus,
  updateCryptoStep,
} from "../../../../store/slices/cryptoSlice";
import { useAppSelector } from "../../../../store/hooks";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH } from "../../../OperateMEP/constants";

// CONNECTORS
import { acceptTermsAndConditions } from "../../../../connectors/cryptoApi";

// COMPONENTS
import { ModalBody, ModalHeader } from "@nextui-org/react";
import { ButtonsFooter } from "../ButtonsFooter/ButtonsFooter";
import { BackHeader } from "../BackHeader/BackHeader";
import Alert from "../../../Alert/Alert";

// STYLES
import styles from "./ConfirmModal.module.scss";

interface Props {
  companyName: string;
  refetch: () => Promise<void>;
}

const ConfirmModal = ({ companyName, refetch }: Props) => {
  const {
    header,
    flexContainer,
    scrollContent,
    description,
    acceptCheck,
    checkbox,
    accountInfoContainer,
    alertText,
  } = styles;

  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);
  const dispatch = useDispatch();
  const [acceptedCheck, setAcceptedCheck] = useState(false);
  const { accounts } = useAppSelector((state) => state.userAccounts);
  const { selectedAccount } = useAppSelector((state) => state.crypto);
  const account = accounts.find((acc) => acc.number === selectedAccount);

  const handleCreateAccount = async () => {
    try {
      await acceptTermsAndConditions([Number(selectedAccount)]);
      refetch();
      dispatch(updateCryptoStatus("SUCCESS"));
    } catch (e) {
      console.error(e);
      dispatch(updateCryptoStatus("ERROR"));
    } finally {
      dispatch(updateCryptoStep("STATUS"));
    }
  };

  const onGoBack = () => dispatch(updateCryptoStep("ACCOUNTS"));

  const alertComponent = (
    <div className={!isMobile ? alertText : description}>
      <p>
        Tené en cuenta que las operaciones con activos digitales son
        irreversibles
      </p>
      <p>
        La operatoria dentro de Allaria Digital implica compra y/o venta de
        títulos públicos en tu cuenta de ALLARIA SA para eficientizar la
        operatoria, por ende dicha operatoria podría limitar el acceso al
        Mercado Libre de Cambios (MLC)
      </p>
    </div>
  );

  return (
    <>
      {isMobile && <BackHeader onGoBack={onGoBack} companyName={companyName} />}

      <ModalHeader className={`${isMobile && "pt-[7rem] pb-[0] px-[1rem]"}`}>
        <span className={header}>Crear cuenta en {companyName}</span>
      </ModalHeader>
      <ModalBody className={`${flexContainer} ${isMobile && "py-[0]"}`}>
        <div className={scrollContent}>
          <span className={description}>
            Es necesario aceptar los Términos y condiciones para poder utilizar
            esta funcionalidad
          </span>

          {isMobile && alertComponent}

          <div className={accountInfoContainer}>
            <p> {isMobile ? "CUENTA" : "Cuenta Comitente"}</p>
            <p>{account?.name}</p>
            <p>
              {isMobile ? "Comitente " : ""}
              {account?.number}
            </p>
          </div>

          {!isMobile && <Alert variant="INFO">{alertComponent}</Alert>}
          <div className={acceptCheck}>
            <input
              type="checkbox"
              className={checkbox}
              checked={acceptedCheck}
              onChange={() => setAcceptedCheck(!acceptedCheck)}
            />
            <span>
              Acepto los{" "}
              <a
                href="https://assets.allaria.dev/digital/terminos_y_condiciones.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Términos y condiciones
              </a>
            </span>
          </div>
        </div>
      </ModalBody>
      <ButtonsFooter
        isMobile={isMobile}
        primaryButton={{
          text: "Crear cuenta",
          handleClick: handleCreateAccount,
          isDisabled: !acceptedCheck,
        }}
        secondaryButton={{
          text: "Volver",
          handleClick: onGoBack,
          isDisabled: false,
        }}
      />
    </>
  );
};

export default ConfirmModal;
